import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Mail, MapPin } from 'lucide-react';
import { navigationItems, organizationInfo, roadmapInfo } from '../mockData';
import sagedLogo from '../assets/saged_logo.png';
import arcoLogo from '../assets/arco_logo.png';

const Layout = ({ children }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation Header */}
      <nav className="nav-header">
        <Link to="/" className="flex items-center" data-testid="header-logo">
          <img src={sagedLogo} alt="Saged Recovery and Wellness Collaborative" className="h-12 w-auto" />
        </Link>
        
        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center gap-1">
          {navigationItems.map((item) => (
            <Link
              key={item.name}
              to={item.path}
              className={`nav-link ${isActive(item.path) ? 'text-green-700 bg-green-50' : ''}`}
            >
              {item.name}
            </Link>
          ))}
        </div>
        
        <div className="hidden md:flex items-center gap-3">
          <Link to="/contact" className="btn-secondary">
            Get Support
          </Link>
          <Link to="/get-involved" className="btn-primary">
            Donate
          </Link>
        </div>
        
        {/* Mobile Menu Button */}
        <button
          onClick={toggleMobileMenu}
          className="md:hidden p-2 rounded-full hover:bg-gray-100 transition-colors"
        >
          {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </nav>
      
      {/* Mobile Navigation Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden fixed top-20 left-0 right-0 bg-white border-b border-gray-200 shadow-lg z-40">
          <div className="px-4 py-4 space-y-3">
            {navigationItems.map((item) => (
              <Link
                key={item.name}
                to={item.path}
                onClick={() => setIsMobileMenuOpen(false)}
                className={`block px-3 py-2 rounded-full text-base font-medium transition-colors ${
                  isActive(item.path) 
                    ? 'text-green-700 bg-green-50' 
                    : 'text-gray-700 hover:text-green-700 hover:bg-gray-50'
                }`}
              >
                {item.name}
              </Link>
            ))}
            <div className="flex gap-2 pt-3">
              <Link 
                to="/contact" 
                onClick={() => setIsMobileMenuOpen(false)}
                className="btn-secondary flex-1 text-center"
              >
                Get Support
              </Link>
              <Link 
                to="/get-involved" 
                onClick={() => setIsMobileMenuOpen(false)}
                className="btn-primary flex-1 text-center"
              >
                Donate
              </Link>
            </div>
          </div>
        </div>
      )}
      
      {/* Main Content */}
      <main className="relative">
        {children}
      </main>
      
      {/* Footer */}
      <footer className="bg-gray-50 border-t border-gray-200">
        <div className="container py-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Organization Info */}
            <div className="lg:col-span-2">
              <div className="mb-4">
                <img src={sagedLogo} alt="Saged Recovery and Wellness Collaborative" className="h-16 w-auto" />
              </div>
              <p className="body-medium mb-6 max-w-md">
                {organizationInfo.mission}
              </p>
              <div className="space-y-2">
                {organizationInfo.phone && (
                  <div className="flex items-center gap-3">
                    <Phone className="h-4 w-4 text-gray-500" />
                    <span className="body-small">{organizationInfo.phone}</span>
                  </div>
                )}
                <div className="flex items-center gap-3">
                  <Mail className="h-4 w-4 text-gray-500" />
                  <span className="body-small">{organizationInfo.email}</span>
                </div>
                <div className="flex items-center gap-3">
                  <MapPin className="h-4 w-4 text-gray-500" />
                  <span className="body-small">{organizationInfo.address}</span>
                </div>
              </div>
              <a
                href={roadmapInfo.certificateFile}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 mt-6 p-3 pr-4 rounded-lg bg-white border border-gray-200 hover:border-green-400 transition-colors"
                data-testid="footer-arco-badge"
              >
                <img src={arcoLogo} alt="ARCO — Alliance for Recovery Centered Organizations" className="h-10 w-auto" />
                <span className="caption font-semibold text-gray-700">{roadmapInfo.membershipLabel}</span>
              </a>
            </div>
            
            {/* Quick Links */}
            <div>
              <h3 className="heading-3 mb-4">Quick Links</h3>
              <ul className="space-y-2">
                {navigationItems.map((item) => (
                  <li key={item.name}>
                    <Link to={item.path} className="body-small hover:text-green-600 transition-colors">
                      {item.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            
            {/* Services */}
            <div>
              <h3 className="heading-3 mb-4">Our Services</h3>
              <ul className="space-y-2 body-small">
                <li>Peer Recovery Coaching</li>
                <li>Recovery Housing Support</li>
                <li>Transportation Services</li>
                <li>Community Programming</li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-200 mt-8 pt-8">
            <p className="caption text-center mb-4" data-testid="footer-ein">
              Saged Recovery and Wellness Collaborative is a 501(c)(3) public charity. EIN: 41-3518085. Donations are tax-deductible to the fullest extent allowed by law.
            </p>
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="caption">
                © 2026 Saged Recovery and Wellness Collaborative, Inc. All rights reserved.
              </p>
              <div className="flex gap-6">
                <Link to="/privacy" className="caption hover:text-green-600 transition-colors">Privacy Policy</Link>
                <Link to="/terms" className="caption hover:text-green-600 transition-colors">Terms of Service</Link>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;